import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ShellDomainProvider } from '../standalone-packages/ecap3/core';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { CaseSeedDataQuery } from '../models/queryModels/CaseSeedDataQuery';
import { CaseSeedDataResults } from '../models/responseModels/CaseSeedDataResults';
import { Observable, tap } from 'rxjs';
import { QueryHandlerResponse } from '../models/responseModels/QueryHandlerResponse';
import { ProcessCaseCommand } from '../models/commandModels/ProcessCaseCommand';
import { InsuranceCase } from '../../app-create-case/models/insurance-case.model';
import { IpexCommandResponse } from '../models/responseModels/IpexCommandResponse';
import { CaseCommonService } from '../../app-create-case/services/case-common.service';
import { map } from 'rxjs/operators';
import { UpdateSLACommand } from '../models/commandModels/UpdateSLACommand';
import { SeedMetaData } from '../models/domain/SeedMetaData';
import { CommonService } from './common.service';
import { TranslateService } from '@ngx-translate/core';
import { SendConsultationCallBackRequestCommand } from '../models/commandModels/SendConsultationCallBackRequestCommand';
import { CaseFoldedState } from '../models/CaseFoldedState';
import { CaseStatusDurationLogEntryModel } from '../models/CaseStatusDurationLogEntryModel';
import { ConnectedWithInsuranceCase, InsuranceConnectedCase } from '../models/responseModels/InsuranceConnectedCase';
import { UrlResolverService } from './url/url-resolver.service';

@Injectable({
    providedIn: 'root'
})
export class CaseService {
    private readonly urlResolver = inject(UrlResolverService);
    private readonly pcxCommandUrl: string = this.urlResolver.getPcxCommandUrl();
    private readonly pcxQueryUrl: string = this.urlResolver.getPcxQueryUrl();
    private readonly getCaseSeedDataUrl: string = `${this.pcxQueryUrl}GetCaseSeedData` as const;
    private readonly processCaseUrl: string = `${this.pcxCommandUrl}ProcessCaseInfo` as const;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly caseCommonService: CaseCommonService,
        private readonly translateService: TranslateService
    ) {}

    public getCaseSeedData(payload: CaseSeedDataQuery): Observable<QueryHandlerResponse<null, CaseSeedDataResults>> {
        return this.httpClient.post<QueryHandlerResponse<null, CaseSeedDataResults>>(this.getCaseSeedDataUrl, payload, {
            headers: SCConfig.HttpCommonHeader,
            withCredentials: true
        });
    }

    /**
     *  Filter complete case document list for their conversion status to download in group
     */
    public getFileIdsByDownloadType(fullDocumentList: any[], downloadType = null) {
        let fileIds = [];

        let nonGarbageFiles = fullDocumentList.filter(file => {
            return !SCConfig.EmailGarbageFileName.includes(file.Name);
        });

        nonGarbageFiles = nonGarbageFiles.map(file => {
            // failsafe if converted file id info is not cloned anyway - can be deleted upon observatuion later
            // Files that is cloned from email attachment tends to have converted fileId in case document data id
            file.ConvertedFileId = file.ConvertedFileId ?? file.CaseDocumentDataId?.replace('ConvertedFileId:', '');
            return file;
        });

        if (downloadType === SCConfig.DOWNLOAD_TYPE.CASE_FILES) {
            // Files that has no converted version + converted ones except their source files
            fileIds = nonGarbageFiles?.filter(file => !file.ConvertedFileId).map(file => file.FileId);
        } else if (downloadType === SCConfig.DOWNLOAD_TYPE.ORIGINAL_FILES_WITH_CONVERTED_FILE) {
            // Original files that has its own converted file
            fileIds = nonGarbageFiles?.filter(file => !!file.ConvertedFileId).map(file => file.FileId);
        } else if (downloadType === SCConfig.DOWNLOAD_TYPE.ORIGINAL_FILES) {
            // All Files without converted ones
            const convertedFileIds = nonGarbageFiles
                ?.filter(file => {
                    return !!file.ConvertedFileId;
                })
                .map(file => {
                    return file.ConvertedFileId;
                });

            fileIds = nonGarbageFiles
                ?.filter(file => !convertedFileIds.includes(file.FileId))
                .map(caseDoc => caseDoc.FileId);
        } else {
            fileIds = nonGarbageFiles.map(caseDoc => caseDoc.FileId);
        }
        return fileIds;
    }

    public toggleDownloadLoading(
        isLoading: boolean,
        isDocumentFileDownloading: boolean[],
        fullDocumentList: any[],
        targetFileIds: any[],
        isFileIdAsIndex: boolean = false
    ): boolean[] {
        const AllCaseFiles = fullDocumentList.map(caseDoc => caseDoc.FileId);

        AllCaseFiles.forEach((fileId, index) => {
            const trueIndex = isFileIdAsIndex ? fileId : index;
            // Setting loading animation on only a valid correspondent file
            if (isLoading) {
                if (fileId && targetFileIds.includes(fileId)) {
                    isDocumentFileDownloading[trueIndex] = isLoading;
                }
            } else {
                // Setting all the rest animation off
                isDocumentFileDownloading[trueIndex] = false;
            }
        });
        return isDocumentFileDownloading;
    }

    public getAllCaseSeedData(): Observable<QueryHandlerResponse<null, CaseSeedDataResults>> {
        const payload = {
            EntityName: 'SeedData'
        };

        return this.getCaseSeedData(payload).pipe(
            tap(response => {
                const languageKey = CommonService.getLanguageKeyFromLang(this.translateService.currentLang);
                this.caseCommonService.caseComponentSeedData.next(
                    response.Results.ComponentSeed.sort((a, b) => {
                        return SeedMetaData.Compare(a, b, languageKey);
                    })
                );
                this.caseCommonService.InsuranceCompanySeedData.next(response.Results.InsuranceCompanySeed);
                this.caseCommonService.caseUnitSeedData.next(response.Results.CaseUnitSeed);
                this.caseCommonService.setServiceProductsSubject(response.Results.ServiceProducts);
                this.caseCommonService.setVatsSubject(response.Results.Vats);
            })
        );
    }

    public processCaseInfo(command: ProcessCaseCommand): Observable<IpexCommandResponse<boolean>> {
        return this.httpClient.post<IpexCommandResponse<boolean>>(this.processCaseUrl, command, {
            headers: SCConfig.HttpCommonHeader,
            withCredentials: true
        });
    }

    public createCase(
        caseFormPayload: InsuranceCase,
        notificationSubscriptionId: string,
        enableAutoCheck: boolean | null = null,
        callBackRequests: SendConsultationCallBackRequestCommand[] = [],
        caseFoldedState: CaseFoldedState = new CaseFoldedState(),
        isServiceRejectOnMail: boolean = false,
        caseStatusDurationLog: CaseStatusDurationLogEntryModel | null = null,
        insuranceConnectedCase: InsuranceConnectedCase
    ): Observable<IpexCommandResponse<boolean>> {
        const payload: ProcessCaseCommand = {
            ActionName: 'process-case',
            Context: 'process-case',
            NotificationSubscriptionId: notificationSubscriptionId,
            ProcessType: 'createcase',
            CasePayload: caseFormPayload,
            EnableAutoCheck: enableAutoCheck,
            ConsultationCallBackRequests: callBackRequests,
            CaseFoldedState: caseFoldedState,
            IsSendRejectionMail: isServiceRejectOnMail,
            CaseStatusDurationLog: caseStatusDurationLog,
            InsuranceConnectedCase: insuranceConnectedCase
        };
        return this.processCaseInfo(payload);
    }

    public updateCase(
        caseFormPayload: InsuranceCase,
        notificationSubscriptionId: string,
        enableAutoCheck: boolean | null = null,
        callBackRequests: SendConsultationCallBackRequestCommand[] = [],
        caseFoldedState: CaseFoldedState = new CaseFoldedState(),
        isServiceRejectOnMail: boolean = false,
        caseStatusDurationLog: CaseStatusDurationLogEntryModel | null = null,
        insuranceConnectedCase: InsuranceConnectedCase
    ): Observable<IpexCommandResponse<boolean>> {
        const payload: ProcessCaseCommand = {
            ActionName: 'process-case',
            Context: 'process-case',
            NotificationSubscriptionId: notificationSubscriptionId,
            ProcessType: 'updatecase',
            CasePayload: caseFormPayload,
            EnableAutoCheck: enableAutoCheck,
            ConsultationCallBackRequests: callBackRequests,
            CaseFoldedState: caseFoldedState,
            IsSendRejectionMail: isServiceRejectOnMail,
            CaseStatusDurationLog: caseStatusDurationLog,
            InsuranceConnectedCase: insuranceConnectedCase
        };
        return this.processCaseInfo(payload);
    }

    public restartSLA(payload: UpdateSLACommand): Observable<IpexCommandResponse<InsuranceCase>> {
        let url = `${this.urlResolver.getPcxCommandUrl()}RestartSLA`;
        return this.httpClient
            .post(url, payload, { headers: SCConfig.HttpCommonHeader, withCredentials: true, observe: 'response' })
            .pipe(
                map((response: any) => {
                    return response?.body ?? null;
                })
            );
    }

    getDuplicateCaseList(filterableInsuranceCases: InsuranceCase[], claimNumber: string): InsuranceCase[] {
        return filterableInsuranceCases?.filter(cases => {
            const currentInsuranceClaim = cases?.InsuranceClaimNumber.toLowerCase();
            return !(!currentInsuranceClaim || !currentInsuranceClaim.startsWith(claimNumber.toLowerCase()));
        });
    }

    public getRepairCasesFromDataBeast(payload): Observable<any> {
        const claimOrderEndpoint = `${ShellDomainProvider['api'].IpexDataBeastService}/databeast/v1/search/claimOrder`;
        return this.httpClient.post<any>(claimOrderEndpoint, payload, {
            headers: SCConfig.HttpCommonHeader
        });
    }

    public getAllRepairUsers(): Observable<any> {
        const claimOrderEndpoint = `${ShellDomainProvider['api'].IpexDataBeastService}/databeast/v1/users`;
        return this.httpClient.get<any>(claimOrderEndpoint, {
            headers: SCConfig.HttpCommonHeader
        });
    }

    public getInsuranceCaseDetails(insuranceCaseId: string): Observable<QueryHandlerResponse<null, InsuranceCase>> {
        const caseDetailsUrl = `${this.pcxQueryUrl}GetInsuranceCaseDetails`;
        const payload = {
            InsuranceCaseId: insuranceCaseId
        };
        return this.httpClient.post<QueryHandlerResponse<null, InsuranceCase>>(
            caseDetailsUrl,
            payload,
            SCConfig.HttpCommonPostOptions
        );
    }

    public GetInsuranceConnectedCase(
        insuranceCaseId: string,
        insuranceClaimNumber: string | null = null,
        connectedInsuranceClaimNumber: string | null = null,
        connectedCaseType: string | null = null
    ): Observable<QueryHandlerResponse<null, InsuranceConnectedCase>> {
        const caseDetailsUrl = `${this.pcxQueryUrl}GetInsuranceConnectedCase`;
        const payload = {
            insuranceCaseId: insuranceCaseId,
            insuranceClaimNumber: insuranceClaimNumber,
            connectedInsuranceClaimNumber: connectedInsuranceClaimNumber,
            connectedCaseType: connectedCaseType
        };
        return this.httpClient.post<QueryHandlerResponse<null, InsuranceConnectedCase>>(
            caseDetailsUrl,
            payload,
            SCConfig.HttpCommonPostOptions
        );
    }
}
